import {
   For,
   Match,
   Show,
   Switch,
   createEffect,
   createMemo,
   createSignal,
   onCleanup,
   onMount,
} from 'solid-js'

import CurrentFastProgressOverview from '../components/current-fast/CurrentFastProgressOverview'
import MetabolicStateChart from '../components/current-fast/MetabolicStateChart'
import type { CurrentFastProgressVisualizationState } from '../components/current-fast/CurrentFastProgressVisualization'
import Select from '../components/ui/Select'
import TextInput from '../components/ui/TextInput'
import { useI18n } from '../i18n'
import { setAccentTheme } from '../state/accentThemeStore'
import { viewPanelState } from '../state/appUiStore'
import {
   adjustFastDuration,
   clearCurrentFastError,
   currentFast,
   currentFastError,
   displayMode,
   formatDuration,
   formatFastEndTimestamp,
   formatFatBurnDisplay,
   formatTargetHours,
   getFatBurnedGrams,
   getMilestonesForElapsed,
   getProgressPhaseKeyForElapsed,
   getProgressCircleBackground,
   initializeCurrentFastState,
   selectedDurationHours,
   setFastDurationHours,
   setFastStartTimeInputValue,
   startTimeInputValue,
   startupSyncLoading,
   toggleDisplayMode,
   toggleFast,
} from '../state/currentFastStore'
import { FAT_PER_CHOCOLATE_GRAMS, getChocolateBars } from '../utils/chocolateBars'
import { formatDateTimeLocalValue } from '../utils/time'

const STARTUP_SYNC_PREVIEW_DURATION_MS = 36 * 60 * 60 * 1000
const STARTUP_SYNC_PREVIEW_LOOP_MS = 2400
const STARTUP_SYNC_PREVIEW_TICK_MS = 80
const STARTUP_SYNC_PREVIEW_BASE_RATIO = 0.08
const STARTUP_SYNC_PREVIEW_FILL_RATIO = 0.035
const TOUCH_ACTIVATION_MAX_MOVE_PX = 12

type TouchActivationPoint = {
   pointerId: number
   x: number
   y: number
}

type Translate = ReturnType<typeof useI18n>

/**
 * Returns the looping placeholder ring fill shown while the startup sync loads.
 *
 * @param now - The current time.
 * @returns The ring fill ratio.
 */
function getStartupPreviewRatio(now: number) {
   const loopProgress =
      (now % STARTUP_SYNC_PREVIEW_LOOP_MS) / STARTUP_SYNC_PREVIEW_LOOP_MS
   const shimmer = 0.5 - Math.cos(loopProgress * Math.PI * 2) / 2
   return STARTUP_SYNC_PREVIEW_BASE_RATIO + shimmer * STARTUP_SYNC_PREVIEW_FILL_RATIO
}

/**
 * Converts burned fat into the chocolate-bar visualization state.
 *
 * @param totalGrams - Fat burned so far.
 * @param t - The translation function.
 * @returns The visualization state.
 */
function getChocolateVisualization(
   totalGrams: number,
   t: Translate,
): CurrentFastProgressVisualizationState {
   const bars = getChocolateBars(totalGrams)
   if (bars.type === 'none') {
      return { type: 'none' }
   }
   if (bars.type === 'message') {
      return {
         type: 'message',
         message: t('First bar on the way! 🍫 = {grams} g', {
            grams: FAT_PER_CHOCOLATE_GRAMS,
         }),
      }
   }
   return {
      ...bars,
      ariaLabel: t('{count} chocolate bars worth of fat burned', {
         count: bars.count + bars.overflow,
      }),
   }
}

/**
 * Derives elapsed/remaining time and progress from the current fast and the
 * live clock. While the startup sync is loading the ring shows a looping
 * placeholder instead.
 *
 * @param now - The live clock.
 * @returns The timing memos.
 */
function createCurrentFastTiming(now: () => number) {
   const elapsedMs = createMemo(() => {
      const activeFast = currentFast()
      return activeFast ? Math.max(0, now() - activeFast.startTime) : 0
   })
   const remainingMs = createMemo(() => {
      const activeFast = currentFast()
      return activeFast ? Math.max(0, activeFast.duration - elapsedMs()) : 0
   })
   const progressRatio = createMemo(() => {
      if (startupSyncLoading()) {
         return getStartupPreviewRatio(now())
      }
      const activeFast = currentFast()
      return activeFast && activeFast.duration > 0 ? elapsedMs() / activeFast.duration : 0
   })
   const milestones = createMemo(() => getMilestonesForElapsed(elapsedMs()))
   const progressPhaseKey = createMemo(() =>
      currentFast() && !startupSyncLoading() ?
         getProgressPhaseKeyForElapsed(elapsedMs())
      :  'blue',
   )
   return { elapsedMs, remainingMs, progressRatio, milestones, progressPhaseKey }
}

/**
 * Derives the text labels and visualization shown around the progress ring.
 *
 * @param timing - The timing memos.
 * @param t - The translation function.
 * @returns The label memos.
 */
function createCurrentFastLabels(
   timing: ReturnType<typeof createCurrentFastTiming>,
   t: Translate,
) {
   const progressLabel = createMemo(() => {
      const loading = startupSyncLoading()
      const activeFast = currentFast()
      if (!loading && !activeFast) {
         return t('0% of 0h')
      }
      return t('Progress percentage label', {
         percentage: Math.floor(timing.progressRatio() * 100),
         target: formatTargetHours(
            loading || !activeFast ?
               STARTUP_SYNC_PREVIEW_DURATION_MS
            :  activeFast.duration,
         ),
      })
   })
   const timerLabel = createMemo(() => {
      if (startupSyncLoading()) {
         return t('Loading…')
      }
      return formatDuration(
         displayMode() === 'remaining' ? timing.remainingMs() : timing.elapsedMs(),
      )
   })
   const fatBurnLabel = createMemo(() => {
      if (startupSyncLoading()) {
         return t('Loading latest cloud state…')
      }
      const activeFast = currentFast()
      if (!activeFast) {
         return ''
      }
      const currentAmount = formatFatBurnDisplay(getFatBurnedGrams(timing.elapsedMs()))
      const totalAmount = formatFatBurnDisplay(getFatBurnedGrams(activeFast.duration))
      return `${currentAmount} / ${totalAmount}`
   })
   const progressVisualizationState = createMemo<CurrentFastProgressVisualizationState>(
      () =>
         startupSyncLoading() || !currentFast() ?
            // Collapse the row without a fast; during one keep its height so
            // the layout does not jump when the first fat-burn message appears.
            { type: 'hidden' }
         :  getChocolateVisualization(getFatBurnedGrams(timing.elapsedMs()), t),
   )
   const endTimeLabel = createMemo(() => {
      const activeFast = currentFast()
      if (startupSyncLoading() || !activeFast) {
         return ''
      }
      return formatFastEndTimestamp(
         activeFast.startTime + activeFast.duration,
         timing.remainingMs(),
      )
   })
   return {
      progressLabel,
      timerLabel,
      fatBurnLabel,
      progressVisualizationState,
      endTimeLabel,
   }
}

/**
 * Makes a button activate on touch/pen pointer-up, so Android cannot drop the
 * click, while suppressing the synthetic click that follows.
 *
 * @param activate - The action to run.
 * @returns Pointer and click handlers to spread on the button.
 */
function createTouchActivation(activate: () => void) {
   const [touchActivationPoint, setTouchActivationPoint] =
      createSignal<TouchActivationPoint | null>(null)
   let ignoreNextClick = false
   let ignoreNextClickTimerId: number | null = null

   /**
    * Cancels the pending click-suppression window.
    */
   const clearIgnoreTimer = () => {
      if (ignoreNextClickTimerId !== null) {
         window.clearTimeout(ignoreNextClickTimerId)
         ignoreNextClickTimerId = null
      }
   }
   onCleanup(clearIgnoreTimer)

   const isTouchPointer = (event: PointerEvent) =>
      event.isPrimary && (event.pointerType === 'touch' || event.pointerType === 'pen')

   return {
      /**
       * Tracks where a touch activation started.
       *
       * @param event - The pointer-down event.
       */
      onPointerDown: (event: PointerEvent) => {
         if (isTouchPointer(event)) {
            setTouchActivationPoint({
               pointerId: event.pointerId,
               x: event.clientX,
               y: event.clientY,
            })
         }
      },
      /**
       * Activates from pointer-up when the touch did not drift into a scroll.
       *
       * @param event - The pointer-up event.
       */
      onPointerUp: (event: PointerEvent) => {
         const startPoint = touchActivationPoint()
         setTouchActivationPoint(null)
         if (!isTouchPointer(event) || startPoint?.pointerId !== event.pointerId) {
            return
         }
         const movement = Math.hypot(
            event.clientX - startPoint.x,
            event.clientY - startPoint.y,
         )
         if (movement > TOUCH_ACTIVATION_MAX_MOVE_PX) {
            return
         }
         ignoreNextClick = true
         clearIgnoreTimer()
         ignoreNextClickTimerId = window.setTimeout(() => {
            ignoreNextClick = false
            ignoreNextClickTimerId = null
         }, 700)
         activate()
      },
      onPointerCancel: () => setTouchActivationPoint(null),
      /**
       * Handles mouse/keyboard clicks, skipping the synthetic click after a touch activation.
       */
      onClick: () => {
         if (ignoreNextClick) {
            ignoreNextClick = false
            clearIgnoreTimer()
            return
         }
         activate()
      },
   }
}

/**
 * Renders the duration and custom start-time inputs shown before a fast starts.
 *
 * @param props - Whether to hide the controls and the clock for the max start time.
 * @returns The controls element.
 */
const FastSetupControls = (props: { hidden: boolean; now: number }) => {
   const t = useI18n()
   return (
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:items-end">
         <div
            id="duration-container"
            class="flex flex-col gap-2 md:h-full md:justify-end"
            classList={{ hidden: props.hidden }}
         >
            <label
               for="fast-duration"
               class="block text-sm font-medium text-ink-muted md:flex-1 md:mb-0"
            >
               {t('Fast Duration (hours)')}
            </label>
            <Select
               id="fast-duration"
               value={selectedDurationHours()}
               onInput={event => {
                  clearCurrentFastError()
                  setFastDurationHours(event.currentTarget.value)
               }}
            >
               <option value="16">{t('16 Hours (16:8)')}</option>
               <option value="18">{t('18 Hours (18:6)')}</option>
               <option value="20">{t('20 Hours (20:4)')}</option>
               <option value="24">{t('24 Hours')}</option>
               <option value="36">{t('36 Hours')}</option>
               <option value="48">{t('2 Days')}</option>
               <option value="72">{t('3 Days')}</option>
            </Select>
         </div>
         <div
            id="start-time-container"
            class="flex flex-col gap-2 md:h-full md:justify-end"
            classList={{ hidden: props.hidden }}
         >
            <label
               for="start-time-input"
               class="block text-sm font-medium text-ink-muted md:flex-1 md:mb-0"
            >
               {t('Custom Start Time (optional)')}
            </label>
            <TextInput
               type="datetime-local"
               id="start-time-input"
               value={startTimeInputValue()}
               max={formatDateTimeLocalValue(props.now)}
               onInput={event => {
                  clearCurrentFastError()
                  setFastStartTimeInputValue(event.currentTarget.value)
               }}
            />
         </div>
      </div>
   )
}

const ADJUST_BUTTON_CLASS =
   'h-12 w-12 rounded-full border border-edge bg-surface-control text-2xl font-semibold text-ink transition hover:bg-surface-control-hover focus:outline-none focus:ring-2 focus:ring-accent-strong disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * Renders the −/start-stop/+ control row.
 *
 * @param props - Visibility flags and the toggle button's label and handlers.
 * @returns The control row element.
 */
const FastControlRow = (props: {
   adjustVisible: boolean
   toggleLabel: string
   toggle: ReturnType<typeof createTouchActivation>
}) => {
   const t = useI18n()
   return (
      <div id="fast-control-row" class="w-full flex items-center justify-center gap-3">
         <button
            id="fast-decrease-btn"
            type="button"
            onClick={() => adjustFastDuration(-1)}
            class={ADJUST_BUTTON_CLASS}
            classList={{ hidden: !props.adjustVisible }}
            aria-label={t('Reduce fast duration by one hour')}
         >
            -
         </button>
         <button
            id="toggle-fast-btn"
            type="button"
            onClick={props.toggle.onClick}
            onPointerDown={props.toggle.onPointerDown}
            onPointerUp={props.toggle.onPointerUp}
            onPointerCancel={props.toggle.onPointerCancel}
            disabled={startupSyncLoading()}
            class="btn-accent w-full max-w-xs text-lg font-bold py-4 rounded-lg transition-all duration-300 ease-in-out text-white shadow-lg"
            classList={{ 'max-w-xs': currentFast() !== null || startupSyncLoading() }}
         >
            {props.toggleLabel}
         </button>
         <button
            id="fast-increase-btn"
            type="button"
            onClick={() => adjustFastDuration(1)}
            class={ADJUST_BUTTON_CLASS}
            classList={{ hidden: !props.adjustVisible }}
            aria-label={t('Extend fast duration by one hour')}
         >
            +
         </button>
      </div>
   )
}

/**
 * Renders the milestone messages, or a placeholder while nothing has been reached.
 *
 * @param props - The reached milestones.
 * @returns The milestones element.
 */
const MilestoneMessages = (props: {
   milestones: ReturnType<typeof getMilestonesForElapsed>
}) => {
   const t = useI18n()
   return (
      <div id="milestone-messages" class="w-full space-y-3">
         <Switch>
            <Match when={startupSyncLoading()}>
               <p class="text-sm text-ink-faint text-center">
                  {t('Loading latest cloud state…')}
               </p>
            </Match>
            <Match when={!currentFast()}>
               <p class="text-sm text-ink-faint text-center">
                  {t('Start a fast to ignite those 🔥 milestones!')}
               </p>
            </Match>
            <Match when={props.milestones.length === 0}>
               <p class="text-sm text-ink-faint text-center">
                  {t('Stay strong! 🔥 Milestones will light up as you progress.')}
               </p>
            </Match>
            <Match when={props.milestones.length > 0}>
               <For each={props.milestones}>
                  {milestone => (
                     <div class="bg-surface-raised border border-edge-soft rounded-lg p-3 shadow-sm">
                        <p class="text-sm font-semibold text-green-300 mb-1">
                           {t(milestone.titleKey)} · {milestone.hours}h
                        </p>
                        <p class="text-sm text-ink leading-relaxed">
                           {t(milestone.messageKey)}
                        </p>
                     </div>
                  )}
               </For>
            </Match>
         </Switch>
      </div>
   )
}

/**
 * Renders the elapsed/remaining toggle and the fast's end time.
 *
 * @param props - Whether an active fast shows them, and the end-time label.
 * @returns The timing details element.
 */
const FastTimingDetails = (props: { visible: boolean; endTimeLabel: string }) => {
   const t = useI18n()
   return (
      <>
         <button
            id="elapsed-mode-toggle"
            type="button"
            onClick={toggleDisplayMode}
            class="block w-full max-w-xs mx-auto mt-2 text-sm font-semibold px-4 py-2 rounded-lg border border-edge bg-surface-control text-ink-soft transition hover:bg-surface-control-hover focus:outline-none focus:ring-2 focus:ring-accent-strong"
            classList={{ hidden: !props.visible }}
            aria-pressed={displayMode() === 'remaining' ? 'true' : 'false'}
         >
            {displayMode() === 'remaining' ?
               t('Show Time Passed')
            :  t('Show Time Remaining')}
         </button>
         <div
            id="fast-end-time"
            class="w-full text-center mt-1 text-sm text-ink-secondary"
            classList={{ hidden: !props.visible }}
         >
            {props.endTimeLabel}
         </div>
      </>
   )
}

/**
 * Renders the current-fast tab (timer, progress ring, controls, milestones).
 *
 * @returns The current-fast view element.
 */
const CurrentFastView = () => {
   const t = useI18n()
   const [now, setNow] = createSignal(Date.now())
   const toggle = createTouchActivation(toggleFast)
   const timing = createCurrentFastTiming(now)
   const labels = createCurrentFastLabels(timing, t)

   onMount(() => {
      initializeCurrentFastState()
   })

   createEffect(() => {
      const tickMs = startupSyncLoading() ? STARTUP_SYNC_PREVIEW_TICK_MS : 1000
      const intervalId = window.setInterval(() => {
         setNow(Date.now())
      }, tickMs)
      onCleanup(() => {
         window.clearInterval(intervalId)
      })
   })

   createEffect(() => {
      setAccentTheme(timing.progressPhaseKey())
   })

   const metabolicTiming = createMemo(() => {
      const activeFast = currentFast()
      return activeFast && !startupSyncLoading() ?
            { elapsedMs: timing.elapsedMs(), targetMs: activeFast.duration }
         :  null
   })
   const controlsHidden = createMemo(() => currentFast() !== null || startupSyncLoading())
   const activeFastControlsVisible = createMemo(
      () => currentFast() !== null && !startupSyncLoading(),
   )
   /**
    * Returns the start/stop button label for the current fast.
    *
    * @returns The button label.
    */
   const toggleButtonLabel = () => {
      if (startupSyncLoading()) {
         return t('Loading…')
      }
      return currentFast() ? t('End Fast') : t('Start Fast')
   }

   return (
      <main
         id="current-view"
         class="view-panel flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:justify-between lg:col-span-2"
         data-view-state={viewPanelState('current')}
      >
         {/* Below lg both columns dissolve into the main flex column so the
             chart can sit between the fast controls and the milestones. */}
         <div class="flex flex-col items-center max-lg:contents">
            <CurrentFastProgressOverview
               active={currentFast() !== null}
               displayMode={displayMode()}
               fatBurnLabel={labels.fatBurnLabel()}
               fatBurnTitle={t('Fat burned')}
               onToggleDisplayMode={toggleDisplayMode}
               progressBackground={getProgressCircleBackground(
                  timing.progressRatio(),
                  timing.elapsedMs(),
               )}
               progressLabel={labels.progressLabel()}
               syncLoading={startupSyncLoading()}
               timerLabel={labels.timerLabel()}
               toggleLabel={t('Toggle timer display mode')}
               visualizationState={labels.progressVisualizationState()}
            />
            <Show when={metabolicTiming()}>
               {metabolic => (
                  <MetabolicStateChart
                     class="max-lg:order-1"
                     elapsedMs={metabolic().elapsedMs}
                     targetMs={metabolic().targetMs}
                  />
               )}
            </Show>
         </div>
         <div class="flex-1 w-full max-w-xl lg:max-w-none lg:space-y-4 max-lg:contents">
            <div class="w-full max-w-xl space-y-4 lg:max-w-none">
               <FastSetupControls hidden={controlsHidden()} now={now()} />
               <FastControlRow
                  adjustVisible={activeFastControlsVisible()}
                  toggleLabel={toggleButtonLabel()}
                  toggle={toggle}
               />
               <FastTimingDetails
                  visible={activeFastControlsVisible()}
                  endTimeLabel={labels.endTimeLabel()}
               />
               <Show when={currentFastError()}>
                  <p class="rounded-lg border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                     {currentFastError()}
                  </p>
               </Show>
            </div>
            <div class="w-full max-w-xl max-lg:order-2 lg:max-w-none">
               <MilestoneMessages milestones={timing.milestones()} />
            </div>
         </div>
      </main>
   )
}

export default CurrentFastView
